import { useCallback, useEffect, useMemo } from 'react';
import { css } from '@emotion/react';
import { CalendarEvent } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/calendar_event.pb';
import { sortBy } from 'lodash-es';
import { ScheduleTypes } from '@frontend/api-schedule';
import { formatDate } from '@frontend/date';
import { GlobalBannerDynamic } from '@frontend/global-info-banner';
import { useTranslation } from '@frontend/i18n';
import { ContentLoader, useModalControl } from '@frontend/design-system';
import { CalendarViewV3 } from '../../components/calendar-view/calendar-view-v3';
import { CalendarViewOffices, OfficeCalendarView } from '../../components/calendar-view/types';
import { CalendarLocationSelectorModal } from '../../components/CalendarLocationSelectorModal';
import { CalendarViewV3Header } from '../../components/CalendarViewV3Header';
import { NoEventsMessage, NoProvidersMessage } from '../../components/EmptyCalendarMessage';
import { EventCardComponent } from '../../components/schedule-calendar-components';
import { useCalendarEventV3Context } from '../../context/CalendarEventsV3Context';
import {
  useAppointmentsInfoShallowStore,
  useCalendarEventsConfigurationShallowStore,
  useGetPractitionerDetailsForCalendarV3,
  useSchedulingLocationInfo,
  useTimezoneBanner,
} from '../../hooks';
import { useGetOfficeHoursDetailsForCalendarV3 } from '../../hooks/scheduler-v3/use-get-office-hours-details-for-calendar-v3';
import { useCalendarV3ShallowStore } from '../../stores/use-calendar-v3-store';
import { HeaderFilterDataType } from '../../types';
import { getFullName, transformV3ResponsesForReviewRequest } from '../../utils';
import { getTimezoneDetails, sortByTimezone } from '../../utils/date-helpers';
import { CalendarEvents } from '../Calendar/EventsPanel/CalendarEvents';
import { CalendarEventType } from '../Calendar/types';
import { isUUID } from '../Calendar/utils';
import { convertV3CalendarEventToAppointmentDetails } from './helpers';

const getFilteredEvents = (
  events: CalendarEvent[],
  providerIds: string[],
  apptTypeIds: string[],
  apptStatus: string
): CalendarEvent[] => {
  const hasProviderFilter = providerIds.length > 0;
  const hasApptTypeFilter = apptTypeIds.length > 0;
  const hasApptStatusFilter = !!apptStatus;

  return events.filter((event) => {
    // Normalize calendar event data
    const attendeeStatus = event?.attendeeStatus?.toLowerCase().replaceAll('_', ' ') || '';
    const providerId = event?.organizerId || '';
    const appointmentTypeId = event?.referenceTypeId || '';

    const matchesProvider = hasProviderFilter ? providerIds.includes(providerId) : true;
    const matchesApptType = hasApptTypeFilter ? apptTypeIds.includes(appointmentTypeId) : true;
    const matchesApptStatus = hasApptStatusFilter
      ? attendeeStatus === apptStatus.toLowerCase().replaceAll('_', ' ')
      : true;

    return matchesProvider && matchesApptType && matchesApptStatus;
  });
};

export const CalendarV3Container = () => {
  const { t } = useTranslation('schedule');
  const { getScopeName, selectedLocationIds, getLocationTimeZone } = useSchedulingLocationInfo();
  const { showTimezoneBanner, dismissTimezoneBanner } = useTimezoneBanner();
  const { setConfiguredCalendarEvent } = useCalendarEventsConfigurationShallowStore('setConfiguredCalendarEvent');
  const { setAppointments } = useAppointmentsInfoShallowStore('setAppointments');

  const locationSelectorModalProps = useModalControl();

  const {
    isLoading,
    practitioners,
    calendarEvents,
    appointmentTypes,
    appointmentStatusList,
    isIntegratedOffice,
    selectedMultiLocationIds,
    hasSelectedMultiLocationIds,
    scheduleEntries,
    scheduleEntriesOfficeData,
    setHasSelectedMultiLocationIds,
    setSelectedMultiLocationIds,
  } = useCalendarEventV3Context();

  const {
    calendarDate,
    calendarLocationIds,
    calendarProviderIds,
    calendarApptTypeIds,
    calendarApptStatus,
    hasCalendarFilters,
  } = useCalendarV3ShallowStore(
    'calendarDate',
    'calendarLocationIds',
    'calendarProviderIds',
    'calendarApptTypeIds',
    'calendarApptStatus',
    'hasCalendarFilters'
  );

  const filteredPractitioners = useMemo(
    () =>
      calendarProviderIds.length
        ? practitioners.filter((practitioner) => calendarProviderIds.includes(practitioner.id))
        : practitioners || [],
    [practitioners, calendarProviderIds]
  );

  const filteredCalendarEventsData = useMemo(
    () =>
      hasCalendarFilters
        ? getFilteredEvents(calendarEvents, calendarProviderIds, calendarApptTypeIds, calendarApptStatus)
        : calendarEvents,
    [calendarEvents, calendarProviderIds, calendarApptTypeIds, calendarApptStatus, hasCalendarFilters]
  );

  const { getPractitionerEventsForCalendarObject } = useGetPractitionerDetailsForCalendarV3({
    calendarEvents: filteredCalendarEventsData,
    practitioners: filteredPractitioners,
    schedules: scheduleEntries,
  });

  const { getOfficeExceptionEventsForCalendarObject, getOfficeWorkingHours, getOfficeAvailabilityExceptions } =
    useGetOfficeHoursDetailsForCalendarV3({
      schedules: scheduleEntriesOfficeData,
    });

  // TODO: Review this logic to handle the configured calendar event
  const handleConfiguredCalendarEvent = (configuredCalendarEvent: CalendarEventType) => {
    const isProviderEvent = configuredCalendarEvent.providerId && isUUID(configuredCalendarEvent.providerId);
    const isOfficeEvent = configuredCalendarEvent.locationId && !configuredCalendarEvent.providerId;
    if (isOfficeEvent || isProviderEvent) {
      setConfiguredCalendarEvent(configuredCalendarEvent);
    }
  };

  const getOfficeCalendarViewObject = (locationId: string): OfficeCalendarView => {
    return {
      officeName: getScopeName(locationId),
      officeTimezone: getLocationTimeZone(locationId),
      officeExceptions: getOfficeExceptionEventsForCalendarObject(locationId),
      hasAnyWorkingHoursSet: !!scheduleEntriesOfficeData.find(
        (schedule) => schedule.locationId === locationId && schedule.id === locationId
      )?.recurrenceRules?.workingHours?.length,
      workingHours: getOfficeWorkingHours(locationId),
      availabilityExceptions: getOfficeAvailabilityExceptions(locationId),
      providers: getPractitionerEventsForCalendarObject(locationId),
      locationId: locationId,
      calendarDateValue: formatDate(calendarDate, 'YYYY-MM-DD'),
    };
  };

  const appointmentTypeListForHeader = useMemo<HeaderFilterDataType[]>(() => {
    return (
      appointmentTypes?.map((appointmentType) => ({
        label: appointmentType.externalName || appointmentType.displayName || '',
        id: appointmentType.id,
        locationId: appointmentType.locationId,
      })) || []
    );
  }, [appointmentTypes]);

  const practitionerListForHeader = useMemo<HeaderFilterDataType[]>(() => {
    return (
      practitioners?.map((practitioner) => ({
        label: getFullName(practitioner),
        id: practitioner.id,
        locationId: practitioner.locationId,
      })) || []
    );
  }, [practitioners]);

  const [defaultAppointmentTypeIds, defaultPractitionerIds] = useMemo<[string[], string[]]>(() => {
    return [
      appointmentTypeListForHeader.map((appointmentType) => appointmentType.id),
      practitionerListForHeader.map((practitioner) => practitioner.id),
    ];
  }, [appointmentTypeListForHeader, practitionerListForHeader]);

  const unconfirmedCalendarEventsCount = useMemo(
    () =>
      filteredCalendarEventsData?.filter(
        (calendarEvent) => calendarEvent?.attendeeStatus?.toLowerCase() === 'unconfirmed'
      ).length || 0,
    [filteredCalendarEventsData]
  );

  const calendarObject = useMemo<CalendarViewOffices>(() => {
    const locationIds = calendarLocationIds.length ? calendarLocationIds : selectedMultiLocationIds;
    if (!!locationIds?.length) {
      return sortByTimezone(
        locationIds.map((locationId) => getOfficeCalendarViewObject(locationId ?? '')),
        'officeTimezone'
      );
    }
    return [getOfficeCalendarViewObject(selectedLocationIds[0])];
  }, [calendarLocationIds, getOfficeCalendarViewObject, selectedLocationIds, selectedMultiLocationIds]);

  const handleMultiLocationSelection = useCallback((locationIds: string[]) => {
    setSelectedMultiLocationIds(locationIds);
    setHasSelectedMultiLocationIds(true);
  }, []);

  // useEffect to update selectedMultiLocationIds when selectedLocationIds changes
  useEffect(() => {
    if (selectedLocationIds && selectedLocationIds.length > 4 && !hasSelectedMultiLocationIds) {
      locationSelectorModalProps.openModal();
    } else if (selectedLocationIds && selectedLocationIds.length <= 4) {
      setSelectedMultiLocationIds(selectedLocationIds);
    }
  }, [selectedLocationIds, hasSelectedMultiLocationIds]);

  // Convert calendarEvents to appointments format to display in event cards and store in appointments store
  useEffect(() => {
    if (calendarEvents?.length > 0) {
      const appointments = convertV3CalendarEventToAppointmentDetails(calendarEvents);
      setAppointments(appointments);
    }
  }, [calendarEvents]);

  const alphabeticallySortedPractitionerList = useMemo(() => {
    const providersListData = transformV3ResponsesForReviewRequest({
      practitionerDataV3: practitioners,
    }).transformedPractitioners as unknown as ScheduleTypes.Provider[];
    return sortBy(providersListData, [(practitioner) => practitioner.lastName?.toLowerCase()]);
  }, [practitioners]);

  const calendarTodayDate = getTimezoneDetails(calendarObject[0].officeTimezone).todayDate;
  return (
    <>
      <section css={containerStyles} id='calendar-view-wrapper'>
        <CalendarViewV3Header
          selectedMultiLocationIds={selectedMultiLocationIds}
          appointmentTypeList={appointmentTypeListForHeader}
          practitionersList={practitionerListForHeader}
          unconfirmedCalendarEventsCount={unconfirmedCalendarEventsCount}
          defaultAppointmentTypeIds={defaultAppointmentTypeIds}
          defaultPractitionerIds={defaultPractitionerIds}
          calendarEventsCount={filteredCalendarEventsData?.length || 0}
          appointmentStatusList={appointmentStatusList}
          calendarTodayDate={calendarTodayDate}
        />

        <div css={{ position: 'relative', overflow: 'auto' }}>
          <ContentLoader
            show={isLoading}
            message={t('Loading appointments...')}
            css={{ justifyContent: 'flex-start', paddingTop: 'calc(50vh - 150px)' }}
          />
          <CalendarViewV3
            data={calendarObject}
            onEventCreate={(startHour, endHour, providerName, providerId, isValid, locationId, calendarDateValue) => {
              handleConfiguredCalendarEvent({
                startHour,
                endHour,
                providerName,
                providerId,
                isValid,
                locationId,
                calendarDateValue,
              });
            }}
            EventCardComponent={EventCardComponent}
            NoProviderComponent={isLoading ? undefined : isIntegratedOffice ? NoEventsMessage : NoProvidersMessage}
            shouldAllowEventCreation={!isIntegratedOffice}
            calendarDate={calendarDate}
          />
        </div>
        <CalendarEvents
          customSelectedLocationIds={selectedMultiLocationIds}
          customProvidersList={alphabeticallySortedPractitionerList}
        />
      </section>

      <CalendarLocationSelectorModal
        modalProps={locationSelectorModalProps.modalProps}
        locationIds={selectedLocationIds}
        updateSelectedLocationIds={handleMultiLocationSelection}
      />

      {showTimezoneBanner && (
        <GlobalBannerDynamic
          id='schedule-timezone-info'
          type='info'
          title={t('Times now displayed in location timezones')}
          message={t(
            "Your calendar now shows appointment times based on each location's timezone, with visual indicators to help you track time differences."
          )}
          onDismiss={dismissTimezoneBanner}
        />
      )}
    </>
  );
};

const containerStyles = css`
  display: flex;
  flex-direction: column;
  height: 100%;
  position: relative;
  container-type: inline-size;
`;
